import { Send } from "@mui/icons-material";
import clsx from "clsx";
import { FC, useState } from "react";
import { useTranslation } from "react-i18next";

import { SVG_MUI } from "@ryde/assets";
import { useModalConfirm } from "@ryde/redux";
import {
  ConfirmResult,
  IRideDto,
  IWaitingListPassengerDto,
  onActivateKeyDown,
  WaitingListStatusEnum,
} from "@ryde/shared";

interface IRideWaitingListRowProps {
  rideDto: IRideDto | undefined;
  row: IWaitingListPassengerDto;
  onSendInvitation: (passengerId: number) => Promise<boolean>;
}

export const RideWaitingListRow: FC<IRideWaitingListRowProps> = ({
  rideDto,
  row,
  onSendInvitation,
}) => {
  const { t } = useTranslation();
  const { confirmDialogResult } = useModalConfirm();
  const [isSending, setIsSending] = useState(false);
  const isRemoved =
    row.status === WaitingListStatusEnum.removedByAdmin ||
    row.status === WaitingListStatusEnum.removedByPassenger;
  const { seatsCount = 0, maxPassengers = 0 } = rideDto?.metadata || {};

  const vacancy = maxPassengers - seatsCount;
  // ASK BUSINESS SIDE. ALTERNATIVE -> const goal = (row?.seatCount || 1) + (row.hasDog ? 1 : 0);
  const goal = row?.seatCount || 1;
  const hasExtraPlace = vacancy && vacancy >= goal;
  const canSend = hasExtraPlace && !row.isNotified && !isRemoved && !isSending;

  const handleSendClick = async (): Promise<void> => {
    if (!canSend) {
      return;
    }
    const confirmationResult = await confirmDialogResult({
      title: t("waitingList.sendInvitationConfirmTitle"),
      message: t("waitingList.sendInvitationConfirmMessage"),
    });
    if (confirmationResult !== ConfirmResult.OK) {
      return;
    }
    setIsSending(true);
    try {
      await onSendInvitation(row.passengerId);
    } finally {
      setIsSending(false);
    }
  };

  return (
    <div
      className={clsx(
        "RideWaitingListPanel-Row",
        isRemoved && "RideWaitingListPanel-Row--removed",
      )}
    >
      <span
        className={clsx(
          "RideWaitingListPanel-Send",
          row.isNotified && "RideWaitingListPanel-Send--sent",
          canSend && "RideWaitingListPanel-Send--clickable",
        )}
        onClick={canSend ? handleSendClick : undefined}
        onKeyDown={canSend ? onActivateKeyDown(handleSendClick) : undefined}
        role={canSend ? "button" : undefined}
        tabIndex={canSend ? 0 : undefined}
        aria-label={t("waitingList.sendInvitationConfirmTitle")}
      >
        <Send className="RideWaitingListPanel-SendIcon" fontSize="small" />
      </span>
      <span className="RideWaitingListPanel-Name">
        {row.passengerFullName || `#${row.passengerId}`}
      </span>
      <span className={"RideWaitingListPanel-Delete"}>
        <SVG_MUI.Trash2SVG
          data-e2e="row_delete_svg"
          className="RideWaitingListPanel-DeleteIcon"
        />
      </span>
    </div>
  );
};
